import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, BarChart3, Landmark } from "lucide-react";
import { ClaimsUpload } from "@/components/upload/ClaimsUpload";
import { UploadHistory } from "@/components/upload/UploadHistory";
import { useProgramAccess } from "@/platform/ProgramAccessContext";
import { useActiveProgram } from "@/platform/useActiveProgram";
import { setHomeProgram } from "@/platform/activeProgram";

/**
 * Program gate (Option A, owner 2026-07-21): Data Management is program-scoped
 * (upload targets + Delete Data follow the active program), so a dual-program
 * user who hasn't picked a program this session must choose before the page
 * silently defaults to MIPS. Picking sets the SESSION program (same
 * homeProgram the sidebar/top-bar use) — the whole app snaps into context.
 * Single-program users never see this.
 */
function ProgramGate() {
  return (
    <div className="max-w-3xl mx-auto mt-8">
      <div className="mb-6 text-center">
        <h2 className="text-xl font-semibold">Which program are you working in?</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Uploads and data deletion are scoped to one program. You can switch any
          time from Home or the selector in the top bar.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Card
          className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-accent/20"
          onClick={() => setHomeProgram('mips')}
        >
          <CardHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">MIPS Registry</CardTitle>
            <CardDescription>Upload claims for your practices</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full">
              Continue in MIPS <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
        <Card
          className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-accent/20"
          onClick={() => setHomeProgram('app-plus')}
        >
          <CardHeader>
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Landmark className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">APP Plus (MSSP)</CardTitle>
            <CardDescription>Upload claims for your ACOs</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full">
              Continue in APP Plus <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function UploadData() {
  const [activeTab, setActiveTab] = useState("upload");
  const { hasProgram } = useProgramAccess();
  const activeProgram = useActiveProgram();

  // Ambiguous context: both programs held, none chosen this session.
  const needsProgramChoice =
    activeProgram === 'neutral' && hasProgram('mips') && hasProgram('app-plus');

  return (
    <div>
      <Helmet>
        <title>Data Management – Upload, History, Reports | VBCA MIPS</title>
        <meta name="description" content="Manage your data uploads, view history, and download reports for MIPS quality measure reporting" />
        <link rel="canonical" href="/programs/mips/upload" />
      </Helmet>

      <h1 className="mb-6 text-3xl font-bold">Data Management</h1>

      {needsProgramChoice ? (
        <ProgramGate />
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">Data Upload</TabsTrigger>
            <TabsTrigger value="history">Upload History</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-6">
            <ClaimsUpload />
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <UploadHistory />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
